import i18n from '../../i18n';
import { FormatDateStringProps, FormattedDateTime } from './types';

class DateUtils {
  static compareDates(timestamp: number, isCommunity = false) {
    const SECOND_IN_MILLISECONDS = 1000;
    const MINUTE_IN_MILLISECONDS = SECOND_IN_MILLISECONDS * 60;
    const HOUR_IN_MILLISECONDS = MINUTE_IN_MILLISECONDS * 60;
    const DAY_IN_MILLISECONDS = HOUR_IN_MILLISECONDS * 24;

    const pastDate = new Date(timestamp);
    const currentDate = new Date();

    const timeDiff = Math.abs(pastDate.getTime() - currentDate.getTime());

    const years = Math.floor(timeDiff / DAY_IN_MILLISECONDS / 365);
    const months = Math.floor(timeDiff / DAY_IN_MILLISECONDS / 30);
    const days = Math.floor(timeDiff / DAY_IN_MILLISECONDS) % 30;
    const hours = Math.floor(timeDiff / HOUR_IN_MILLISECONDS);
    const minutes = Math.floor(timeDiff / MINUTE_IN_MILLISECONDS);
    const seconds = Math.floor(timeDiff / SECOND_IN_MILLISECONDS);

    const postOrCommunity = isCommunity
      ? 'community_changes.last_activity'
      : 'posting_time.last_activity';

    const postOrCommunityMoreMonth = isCommunity
      ? 'community_changes.last_activity_more_month'
      : 'posting_time.last_activity';

    const unitOfTime = {
      year: i18n.t(postOrCommunityMoreMonth, {
        time_value: years,
        unit_of_time: i18n.t('unit_of_time.year')
      }),
      years: i18n.t(postOrCommunityMoreMonth, {
        time_value: years,
        unit_of_time: i18n.t('unit_of_time.years')
      }),
      month: i18n.t(postOrCommunity, {
        time_value: months,
        unit_of_time: i18n.t('unit_of_time.month')
      }),
      months: i18n.t(postOrCommunityMoreMonth, {
        time_value: months,
        unit_of_time: i18n.t('unit_of_time.months')
      }),
      day: i18n.t(postOrCommunity, {
        time_value: days,
        unit_of_time: i18n.t('unit_of_time.day')
      }),
      days: i18n.t(postOrCommunity, {
        time_value: days,
        unit_of_time: i18n.t('unit_of_time.days')
      }),
      hour: i18n.t(postOrCommunity, {
        time_value: hours,
        unit_of_time: i18n.t('unit_of_time.hour')
      }),
      hours: i18n.t(postOrCommunity, {
        time_value: hours,
        unit_of_time: i18n.t('unit_of_time.hours')
      }),
      minute: i18n.t(postOrCommunity, {
        time_value: minutes,
        unit_of_time: i18n.t('unit_of_time.minute')
      }),
      minutes: i18n.t(postOrCommunity, {
        time_value: minutes,
        unit_of_time: i18n.t('unit_of_time.minutes')
      }),
      second: i18n.t(postOrCommunity, {
        time_value: seconds,
        unit_of_time: i18n.t('unit_of_time.second')
      }),
      seconds: i18n.t(postOrCommunity, {
        time_value: seconds,
        unit_of_time: i18n.t('unit_of_time.seconds')
      })
    };

    // time units greater than one day
    if (years === 1) return unitOfTime.year;
    if (years > 1) return unitOfTime.years;
    if (months === 1) return unitOfTime.month;
    if (months > 1) return unitOfTime.months;
    if (days === 1) return unitOfTime.day;
    if (days > 1) return unitOfTime.days;

    // time units for day
    if (years === 0 && months === 0 && days === 0) {
      if (hours === 1) return unitOfTime.hour;
      if (hours > 1) return unitOfTime.hours;
      if (minutes === 1) return unitOfTime.minute;
      if (minutes > 1) return unitOfTime.minutes;
      if (seconds === 1) return unitOfTime.second;
      if (seconds > 1) return unitOfTime.seconds;
    }

    return i18n.t('unit_of_time.now');
  }

  static convertDateToUTC(d: Date) {
    return new Date(
      Date.UTC(
        d.getUTCFullYear(),
        d.getUTCMonth(),
        d.getUTCDate(),
        d.getUTCHours(),
        d.getUTCMinutes(),
        d.getUTCSeconds(),
        d.getUTCMilliseconds()
      )
    );
  }

  static convertStringToISODate(d: string): string | null {
    if (!d.includes('/')) {
      return null;
    }

    const date = d?.split('/')?.reverse()?.join('-');

    return date ?? null;
  }

  static formatDateAndTime({
    dateString,
    timeZone,
    locale
  }: FormatDateStringProps): FormattedDateTime | null {
    const options = {
      year: undefined,
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: undefined,
      timeZone
    } as Intl.DateTimeFormatOptions;

    try {
      const [d, t] = new Intl.DateTimeFormat(locale, options)
        .format(new Date(dateString))
        .split(',');
      return {
        date: d.trim(),
        time: t.trim()
      };
    } catch {
      return null;
    }
  }
}

export default DateUtils;
